// ABOUTME: Handles invoking Zapier actions and polling for results
// ABOUTME: Includes retry logic and param normalization for API quirks
import { backOff } from "exponential-backoff";

import { AgentsZapierFacade } from ".";
import {
  ActionType,
  ConnectionId,
  InvokeAsyncResponse,
  InvokeRequest,
  InvokeResponse,
  ZapierFacadeError,
} from "./types";
import { prepareParamsForSend } from "./util";

const ZAP_SEARCH_SUCCESS_ON_MISS = "_zap_search_success_on_miss" as const;

/** Custom error type that signals "please retry" */
class InvocationNotReadyError extends Error {}

export const invokeAction = async (
  facade: AgentsZapierFacade,
  {
    selectedApi,
    action,
    actionType,
    params: bodyParams,
    authenticationId,
  }: {
    selectedApi: string;
    action: string;
    actionType: ActionType;
    params: Record<string, unknown>;
    authenticationId?: ConnectionId;
  },
): Promise<InvokeAsyncResponse> => {
  const invokeRequest: InvokeRequest = {
    selected_api: selectedApi,
    action,
    type_of: actionType,
    params: prepareParamsForSend(bodyParams, action),
    authentication_id: authenticationId,
    is_sampling: false,
    test_poll: false,
    sync: false,
    is_bulk_read: false,
    destination_selected_apis: [],
  };

  if (actionType === "search") {
    invokeRequest.params[ZAP_SEARCH_SUCCESS_ON_MISS] = true;
  }

  return await backOff(
    async () => {
      const { data, error, response } = await facade.api.POST(
        "/api/v4/implementations/invoke/",
        { body: invokeRequest },
      );

      if (error || !data.success) {
        console.info("Zapier API error. Retrying...");
        throw new ZapierFacadeError(
          "/api/v4/implementations/invoke/",
          "POST",
          invokeRequest,
          response,
          error?.errors,
        );
      }

      return data;
    },
    {
      numOfAttempts: 2,
    },
  );
};

type FetchOptionsForInvocation = {
  params: {
    path: { invocation_id: string };
  };
};

/**
 * Polls the invocation results with a backoff until the results are ready.
 */
export const getInvocationResults = async (
  facade: AgentsZapierFacade,
  { invocationId }: { invocationId: string },
): Promise<InvokeResponse> => {
  const startTime = Date.now();
  // Outer backoff: keeps retrying on InvocationNotReadyError
  return backOff(
    async () => {
      // Inner backoff: only retries on ZapierFacadeError
      return backOff(
        async () => {
          const { response, data, error } = await facade.api.GET<
            "/api/v4/implementations/invoke/{invocation_id}/",
            FetchOptionsForInvocation
          >("/api/v4/implementations/invoke/{invocation_id}/", {
            params: { path: { invocation_id: invocationId } },
          });

          if (response.status === 202) {
            console.info("Invocation results not ready (202).");
            throw new InvocationNotReadyError(
              "Invocation results not ready (202).",
            );
          }
          if (response.status === 404) {
            console.info("Invocation results not ready (404).");
            throw new InvocationNotReadyError(
              "Invocation results not ready (404).",
            );
          }
          if (error || !data.success) {
            console.info("Zapier API error. Retrying...");
            throw new ZapierFacadeError(
              `/api/v4/implementations/invoke/${invocationId}/`,
              "GET",
              { invocationId },
              response,
              error?.errors,
            );
          }

          const typedData = data as InvokeResponse;
          if (
            typedData?.status === "buffered" ||
            typedData?.status === "delayed"
          ) {
            console.info(
              `Invocation still in progress (status: ${typedData.status}).`,
            );
            throw new InvocationNotReadyError(
              `Invocation in progress (status: ${typedData.status}).`,
            );
          }

          return typedData;
        },
        {
          numOfAttempts: 2,
          retry: (err: any) => err instanceof ZapierFacadeError,
        },
      );
    },
    {
      // Retry on InvocationNotReadyError with a hard timeout of 5 minutes
      // TODO(NIK): We should use attempts metrics to avoid any wasteful delays.
      // NOTE: This is an exponential backoff schedule:
      // startingDelay = 100ms (default)
      // timeMultiple = 2 (default)
      // delayFirstAttempt = true (wait 100ms before first retry)
      // maxDelay = 45 seconds
      jitter: "full",
      numOfAttempts: Infinity,
      delayFirstAttempt: true,
      maxDelay: 1 * 45 * 1000,
      retry: (err: any) => {
        const currentTime = Date.now();
        // team-edge identified a systemic ~10-minute delay in some buffered flows (SQS visibility
        // timeout + fallback task). During that window, the invoke is healthy but not yet marked done.
        // see: https://zapier.slack.com/archives/C01A1KRNYKU/p1761944896706209?thread_ts=1760987670.803679&cid=C01A1KRNYKU
        if (currentTime - startTime > 12 * 60 * 1000) {
          throw new ZapierFacadeError(
            `/api/v4/implementations/invoke/${invocationId}/`,
            "GET",
            { invocationId },
            {
              status: 504,
              statusText: "Gateway Timeout",
            } as Response,
            [
              `Failed to get invocation results after ${
                currentTime - startTime
              }ms`,
            ],
          );
        }
        if (err instanceof InvocationNotReadyError) {
          return true;
        }
        return false;
      },
    },
  );
};
